import { useEffect, useState } from 'react';
import { commissionsApi } from '@/lib/api';
import { Commission } from '@/types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const statusStyles: Record<string, string> = {
  pending: 'badge-warning',
  approved: 'badge-info',
  paid: 'badge-success',
  rejected: 'badge-danger',
  refunded: 'badge-neutral',
};

export default function OrgCommissions() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const fetchCommissions = async () => {
    setIsLoading(true);
    try {
      const params: { status?: string } = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      const response = await commissionsApi.list(params);
      if (response.data?.success) {
        setCommissions(response.data?.data?.commissions ?? []);
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [filter]);

  const handleApprove = async (commissionId: string) => {
    try {
      await commissionsApi.approve(commissionId);
      toast.success('Commission approved');
      fetchCommissions();
    } catch {
      toast.error('Failed to approve commission');
    }
  };

  const handleReject = async (commissionId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      await commissionsApi.reject(commissionId, reason || undefined);
      toast.success('Commission rejected');
      fetchCommissions();
    } catch {
      toast.error('Failed to reject commission');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select commissions to approve');
      return;
    }

    try {
      await commissionsApi.bulkApprove(selectedIds);
      toast.success(`${selectedIds.length} commissions approved`);
      setSelectedIds([]);
      fetchCommissions();
    } catch {
      toast.error('Failed to approve commissions');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Defensive check for commissions array
  const safeCommissions = commissions || [];

  const toggleSelectAll = () => {
    const pendingIds = safeCommissions.filter(c => c.status === 'pending').map(c => c.commissionId);
    if (selectedIds.length === pendingIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingIds);
    }
  };

  const pendingCount = safeCommissions.filter(c => c.status === 'pending').length;
  const approvedCount = safeCommissions.filter(c => c.status === 'approved').length;
  const paidCount = safeCommissions.filter(c => c.status === 'paid').length;
  const pendingAmount = safeCommissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.commissionAmount, 0);
  const approvedAmount = safeCommissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + c.commissionAmount, 0);
  const paidAmount = safeCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.commissionAmount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal">Commissions</h2>
          <p className="text-charcoal/60 mt-1">Review and manage affiliate commissions</p>
        </div>
        {selectedIds.length > 0 && (
          <button onClick={handleBulkApprove} className="btn btn-success">
            <CheckIcon className="w-5 h-5 mr-2" />
            Approve Selected ({selectedIds.length})
          </button>
        )}
      </div>

      {/* Summary Cards with accent bars */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-tan" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Pending Review</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(pendingAmount)}</p>
            <p className="text-xs text-charcoal/50 mt-1">{pendingCount} commissions</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-rust" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Approved</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(approvedAmount)}</p>
            <p className="text-xs text-charcoal/50 mt-1">{approvedCount} awaiting payout</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-sage" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Paid Out</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(paidAmount)}</p>
            <p className="text-xs text-charcoal/50 mt-1">{paidCount} total paid</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'paid', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => { setFilter(status); setSelectedIds([]); }}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filter === status
                ? 'bg-rust text-white'
                : 'bg-warm-white text-charcoal/70 hover:bg-cream'
            )}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
          </div>
        ) : safeCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No commissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50 border-b border-tan/30">
                <tr>
                  {pendingCount > 0 && filter === 'pending' && (
                    <th className="px-6 py-3 w-12">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === pendingCount}
                        onChange={toggleSelectAll}
                        className="rounded border-tan bg-warm-white text-rust focus:ring-rust"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Affiliate</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Order Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-charcoal uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/20">
                {safeCommissions.map((commission) => (
                  <tr key={commission.commissionId} className="hover:bg-cream/30">
                    {pendingCount > 0 && filter === 'pending' && (
                      <td className="px-6 py-4">
                        {commission.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(commission.commissionId)}
                            onChange={() => toggleSelect(commission.commissionId)}
                            className="rounded border-tan bg-warm-white text-rust focus:ring-rust"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 text-charcoal/70">{format(new Date(commission.createdAt), 'MMM d, yyyy')}</td>
                    <td className="px-6 py-4">
                      <code className="text-xs text-charcoal/60">
                        {commission.affiliateId.slice(0, 8)}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-charcoal/80">{commission.orderId}</code>
                    </td>
                    <td className="px-6 py-4 text-charcoal/70">{formatCurrency(commission.orderAmount)}</td>
                    <td className="px-6 py-4">
                      <span className="font-serif font-medium text-charcoal">
                        {formatCurrency(commission.commissionAmount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[commission.status])}>
                        {commission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {commission.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleApprove(commission.commissionId)}
                            className="p-2 text-sage hover:bg-sage/10 rounded-lg"
                            title="Approve"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleReject(commission.commissionId)}
                            className="p-2 text-rust hover:bg-rust/10 rounded-lg"
                            title="Reject"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
